import type { ComponentProps } from 'react'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import Clickable from 'components/Common/Clickable'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { getLocalDate } from 'lib/datetime/calendarUtils'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TextLink from 'LuxKit/TextLink'
import FormatCurrency from 'components/Common/FormatCurrency'
import SolidUsdCircleIcon from 'LuxKit/Icons/solid/SolidUsdCircleIcon'
import Group from 'components/utils/Group'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import LineFireIcon from 'LuxKit/Icons/line/LineFireIcon'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_DATE_SHORT_DAY_NAME, INTL_LONG_WEEKDAY } from 'constants/dateFormats'
import { pluralizeToString } from 'lib/string/pluralize'
import { getThemeClassName } from 'lib/theme/themeUtils'
import PaletteSheet from 'LuxKit/PaletteSheet'
import OfferCalendarPickerRecommendationsModal from '../CalendarPicker/OfferCalendarPickerRecommendationsModal'
import ModalContext from 'contexts/ModalContext'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import { useIntl } from 'react-intl'

const POLYGON_WIDTH = 16

const DateText = styled(PaletteSheet)`
  width: ${rem(72)};
  height: ${rem(60)};
  display: flex;
  justify-content: center;
  align-items: center;
  border-right: 1px solid ${props => props.theme.palette.neutral.default.five};
  background-color: inherit;

  &.is-complementary-check-in {
    background-color: ${props => props.theme.palette.neutral.default.eight};

    &.check-out {
      background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
    }
  }

  &.is-complementary-check-out {
    background-color: ${props => props.theme.palette.neutral.default.eight};
  }
`

const Main = styled(PaletteSheet)`
  --polygon-width: ${rem(POLYGON_WIDTH)};
  --padding: ${rem(8)};
  --extra-padding: calc(var(--padding) + var(--polygon-width));

  position: relative;
  padding: var(--padding);
  height: ${rem(60)};
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;

  &.row-direction {
    flex-wrap: wrap;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
  }

  /* For Bundle & Save polygon */
  ::before {
    content: '';
    top: 0;
    left: 0;
    position: absolute;
    display: none;
    clip-path: polygon(0 0, 100% 0, 0 100%);
    width: var(--polygon-width);
    height: 100%;
  }

  &.is-complementary-check-in {
    padding-left: var(--extra-padding);

    &::before {
      display: hidden;
    }
  }

  &.is-complementary-check-out {
    padding-left: var(--extra-padding);

    &::before {
      display: block;
      background-color: ${props => props.theme.palette.neutral.default.one};
    }
  }
`

const CellWrapper = styled(Clickable)`
  --polygon-width: ${rem(POLYGON_WIDTH)};

  display: flex;
  position: relative;
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
  transition: background-color 0.2s, color 0.2s;

  &:disabled, &.other-availability {
    background-color: ${props => props.theme.palette.neutral.default.six};
  }

  &.unselectable {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }

  &.check-in, &.check-out {
    box-shadow: none;
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};

    &.is-complementary-check-in {
      background-color: ${props => props.theme.palette.neutral.default.eight};
    }
  }

  &.between-day {
    background-color: ${props => props.theme.palette.highlight.primary.lightBackground}
  }
`

const SoldOutRecommendationsLink = styled(TextLink)`
  left: ${rem(4)};
  position: relative;
`

const PlusFlightsText = styled(Caption)`
  margin-left: ${rem(4)};
`

const CheckInOutText = styled(BodyText)`
  position: absolute;
  right: ${rem(16)};
`

const Adornment = styled.div`
  display: grid;
  place-items: center;
  padding: ${rem(8)};
`

interface Props {
  isCheckIn?: boolean
  isCheckOut?: boolean
  duration: number
  day: App.CalendarDay
  hasOtherAvailability: boolean
  enableCalendarRecommendations: boolean
  style?: React.CSSProperties
  isRoomSelected?: boolean
  className?: string
  currentOfferId?: string
  flights?: boolean
  isBetweenDay?: boolean
  bundleOfferId?: string
  isComplementaryBundleCell?: boolean
  isComplementaryCheckIn?: boolean
  isComplementaryCheckOut?: boolean
  isIllegalBundleSelection?: boolean
  complementaryPropertyName?: string
  flightsOnly?: boolean
  hasPromo?: boolean
  isTacticalPromotion?: boolean
  onClick?: (day: App.CalendarDay) => void
  cheapestDay?: App.CalendarCheapestDay
  onInvalidBundleDateClick: () => void
  showUrgencyLabels?: boolean
  displayMemberPrice: boolean
}

enum AvailabilityVariant {
  Available,
  AvailableInOtherRoomTypes,
  CanSwitchToHotel,
  ShowRecommendations,
  SoldOut,
}

function OfferBundleAndSaveCalendarSidebarDayCell(props: Props) {
  const {
    duration,
    day,
    hasOtherAvailability,
    enableCalendarRecommendations,
    isCheckIn,
    isCheckOut,
    style,
    isRoomSelected,
    className,
    flights,
    isBetweenDay,
    flightsOnly,
    bundleOfferId,
    currentOfferId,
    isComplementaryBundleCell,
    isComplementaryCheckIn = false,
    isComplementaryCheckOut = false,
    isIllegalBundleSelection = false,
    complementaryPropertyName,
    hasPromo,
    isTacticalPromotion = false,
    onClick,
    cheapestDay,
    onInvalidBundleDateClick,
    showUrgencyLabels,
    displayMemberPrice,
  } = props

  const intl = useIntl()
  const dispatch = useOfferPageDispatch()
  const intlDateFormatter = useIntlDateFormatter()
  const isFromPrice = !isRoomSelected
  const price = flights ? day.packagedTotal : day.hotelTotal
  const memberPrice = flights ? day.packagedMemberTotal : day.hotelMemberTotal
  const displayPrice = displayMemberPrice ? memberPrice : price
  const flightPriceAvailable = day.flightPrice > 0
  const displayFlightsInfo = flights && flightPriceAvailable
  const available = day.hotelAvailable && (!flights || day.flightsAvailable)
  const isComplementaryBetweenCell = isComplementaryBundleCell && !(isComplementaryCheckIn || isComplementaryCheckOut)
  const hideCellContent = (flightsOnly && !flightPriceAvailable) || isComplementaryBetweenCell
  const canSwitchToHotel = day.hotelAvailable && flights && !flightsOnly
  const date = getLocalDate(day.checkIn)
  const greyDarkTextType = !isCheckIn ? 'neutral-three' : undefined
  const showUrgencyLabel = showUrgencyLabels && isRoomSelected && !isBetweenDay && !isCheckIn && !isCheckOut
  const isSelected = isBetweenDay || isCheckIn || isCheckOut
  const isInvalidBundleDate = !isComplementaryCheckOut && isIllegalBundleSelection

  const colour = useMemo<ComponentProps<typeof BodyText>['colour']>(() => {
    if (hasPromo) {
      return 'highlight-secondary'
    } else if (displayMemberPrice && !(isCheckIn || isCheckOut)) {
      return 'lux-plus'
    } else {
      return undefined
    }
  }, [hasPromo, displayMemberPrice, isCheckIn, isCheckOut])

  const showModal = useContext(ModalContext)
  const onCellClick = useCallback(() => {
    if (isCheckIn) { return }
    if (isInvalidBundleDate) {
      onInvalidBundleDateClick()
      return
    }
    if (!available) {
      if (canSwitchToHotel) {
        dispatch({ type: OfferPageActions.setFlights, flights: false })
      } else if (hasOtherAvailability) {
        dispatch({ type: OfferPageActions.toggleSubFilter, filter: 'room' })
        dispatch({ type: OfferPageActions.setCheckInDate, checkIn: day.checkIn, offerId: currentOfferId })
      } else if (enableCalendarRecommendations) {
        showModal(<OfferCalendarPickerRecommendationsModal checkIn={day.checkIn} />)
      } else {
        dispatch({ type: OfferPageActions.setCheckInDate, checkIn: day.checkIn, offerId: currentOfferId })
      }
    } else {
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: day.checkIn, offerId: currentOfferId })
    }
    onClick?.(day)
  }, [showModal, isCheckIn, isInvalidBundleDate, available, onClick, day, onInvalidBundleDateClick, canSwitchToHotel, hasOtherAvailability, enableCalendarRecommendations, dispatch, currentOfferId])

  const availabilityVariant = useMemo<AvailabilityVariant>(() => {
    if (available) {
      return AvailabilityVariant.Available
    }
    if (canSwitchToHotel) {
      return AvailabilityVariant.CanSwitchToHotel
    }
    if (hasOtherAvailability) {
      return AvailabilityVariant.AvailableInOtherRoomTypes
    }
    if (enableCalendarRecommendations) {
      return AvailabilityVariant.ShowRecommendations
    }

    return AvailabilityVariant.SoldOut
  }, [available, canSwitchToHotel, hasOtherAvailability, enableCalendarRecommendations])

  const bundleAdditionalAmount = useMemo(() => {
    if (cheapestDay) {
      const diff = day.hotelTotal - cheapestDay.hotelTotal
      if (diff >= 1) {
        return diff
      }
    }
    if (day.surcharge > 0) {
      return day.surcharge
    }
    return null
  }, [cheapestDay, day])

  const isPriceWithFlights = !!(flights && flightPriceAvailable)
  const showFrom = !!(isFromPrice || (flights && !flightPriceAvailable))
  // TODO: i18n - complex pricing with trailing/leading spaces
  const priceDescriptionKey = `${pluralizeToString('night', duration)}${isPriceWithFlights ? ' + flights' : ''} ${showFrom ? 'from ' : ''}` as const

  const checkInLabel = intl.formatMessage({
    id: 'PU8hSPrXhQ',
    defaultMessage: 'Check in',
    description: 'Calendar day cell check-in label',
  })
  const checkOutLabel = intl.formatMessage({
    id: 'gOlUG+/tku',
    defaultMessage: 'Check out',
    description: 'Calendar day cell check-out label',
  })
  const flightsNotAvailableLabel = intl.formatMessage({
    id: '2+xCHdcE9S',
    defaultMessage: 'Flights not available.',
    description: 'Message shown when flights are not available for the selected date',
  })
  const viewHotelPricesLabel = intl.formatMessage({
    id: 'J5hpQevZ1j',
    defaultMessage: 'View hotel prices',
    description: 'Link to view hotel-only prices when flights are unavailable',
  })
  const checkInNotAvailableLabel = intl.formatMessage({
    id: 'Kq5jLYpXGr',
    defaultMessage: 'Check In Not Available',
    description: 'Message shown when check-in is not available for the selected date',
  })
  const soldOutLabel = intl.formatMessage({
    id: 'CkbavYSM5n',
    defaultMessage: 'Sold out',
    description: 'Message shown when a calendar date is sold out',
  })
  const viewSimilarOffersLabel = intl.formatMessage({
    id: '9DQ/2Ekika',
    defaultMessage: 'View similar offers',
    description: 'Link to view similar offers when a date is sold out',
  })
  const availableInOtherLabel = intl.formatMessage({
    id: 'm1PXeEg4/a',
    defaultMessage: 'Available in other',
    description: 'Message prefix indicating availability in other room types',
  })
  const roomTypesLabel = intl.formatMessage({
    id: 'doamm1eo7n',
    defaultMessage: 'room types',
    description: 'Link to view other available room types',
  })

  let FireIcon
  if (isLPCTacticalEnabled()) {
    FireIcon = <SolidFireIcon size="XS" colour="urgency" />
  } else {
    FireIcon = <LineFireIcon size="XS" colour="highlight-secondary" />
  }

  return (
    <CellWrapper
      style={style}
      data-testid={clsx({
        'available-day-cell': available,
      })}
      className={clsx(className, getThemeClassName({ mobile: isComplementaryCheckIn || isComplementaryBetweenCell ? 'inverse' : 'default' }), {
        unselectable: isInvalidBundleDate,
        'other-availability': !available && hasOtherAvailability,
        'check-in': isCheckIn,
        'check-out': isCheckOut,
        'between-day': isBetweenDay,
        'is-complementary': isComplementaryBundleCell && (isCheckIn || isCheckOut),
        'is-complementary-check-in': isComplementaryCheckIn,
        'is-complementary-check-out': isComplementaryCheckOut,
      })}
      disabled={
        availabilityVariant === AvailabilityVariant.SoldOut ||
        (availabilityVariant === AvailabilityVariant.ShowRecommendations && (isBetweenDay || isCheckOut))
      }
      onClick={onCellClick}
    >
      <DateText
        paletteType={isComplementaryBundleCell ? 'inverse' : 'default'}
        data-testid={clsx({
          [`d-${day.checkIn}`]: available,
        })}
        className={clsx({
          'check-in': isCheckIn,
          'check-out': isCheckOut,
          'is-complementary-check-in': isComplementaryCheckIn,
          'is-complementary-check-out': isComplementaryCheckOut,
        })}
      >
        <Heading as="span" variant="heading6">
          <abbr title={intlDateFormatter(date, INTL_LONG_WEEKDAY)}>
            {intlDateFormatter(date, INTL_DATE_SHORT_DAY_NAME)}
          </abbr>
        </Heading>
        <CheckInOutText variant="small" weight="bold">
          {isCheckIn && checkInLabel}
          {isCheckOut && checkOutLabel}
        </CheckInOutText>
      </DateText>

      {(!isCheckOut || isComplementaryBetweenCell || isComplementaryCheckIn) && !isBetweenDay && <Main
        paletteType={isComplementaryCheckIn || isComplementaryBetweenCell ? 'inverse' : 'default'}
        className={clsx({
          'row-direction': availabilityVariant === AvailabilityVariant.ShowRecommendations,
          'check-in': isCheckIn,
          'check-out': isCheckOut,
          'is-complementary-check-in': isComplementaryCheckIn,
          'is-complementary-check-out': isComplementaryCheckOut,
        })}
      >
        {isComplementaryCheckIn && complementaryPropertyName && <BodyText variant="small" underline colour="neutral-one">
          {complementaryPropertyName}
        </BodyText>}
        {!isComplementaryCheckIn && <>
          {availabilityVariant === AvailabilityVariant.Available && !hideCellContent && <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
            <Group direction="vertical">
              <Caption variant="medium" colour={greyDarkTextType}>
                {priceDescriptionKey}
              </Caption>
              {bundleOfferId && bundleAdditionalAmount && (
                // TODO: i18n - complex pricing with trailing/leading spaces
                <BodyText as="span" variant="medium" weight="bold" data-testid="day-price">
                  from +<FormatCurrency value={bundleAdditionalAmount} /> *
                </BodyText>
              )}
              {/* TODO: i18n - complex pricing with trailing/leading spaces */}
              {!bundleOfferId && <BodyText colour={colour} variant="large" weight="bold" data-testid="day-price">
                <FormatCurrency value={displayPrice} />
                {(displayFlightsInfo || !flights) &&
                  <Caption variant="medium" weight="normal" as="span">
                    {' '}total{flights && '*'}
                  </Caption>}
              </BodyText>}
              {/* TODO: i18n - complex pricing */}
              {flights && !flightPriceAvailable &&
                <PlusFlightsText
                  variant="medium"
                  weight="bold"
                  forwardedAs="span"
                >
                  + flights
                </PlusFlightsText>}

            </Group>
            {isTacticalPromotion && !isSelected && FireIcon}
          </Group>}
          {availabilityVariant === AvailabilityVariant.CanSwitchToHotel && <>
            <Caption variant="large" colour="neutral-three">{flightsNotAvailableLabel}</Caption>
            <TextLink size="small">{viewHotelPricesLabel}</TextLink>
          </>}
          {availabilityVariant === AvailabilityVariant.SoldOut && <Caption variant="medium" colour={greyDarkTextType}>{checkInNotAvailableLabel}</Caption>}
          {availabilityVariant === AvailabilityVariant.ShowRecommendations && (
            <>
              <BodyText variant="small" colour="neutral-two">{soldOutLabel}</BodyText>
              {!currentOfferId && <SoldOutRecommendationsLink size="small">{viewSimilarOffersLabel}</SoldOutRecommendationsLink>}
            </>
          )}
          {availabilityVariant === AvailabilityVariant.AvailableInOtherRoomTypes && <Caption variant="medium" colour={greyDarkTextType}>
            <>
              <Caption variant="large" colour="neutral-three">{availableInOtherLabel}</Caption>
              <TextLink size="small">{roomTypesLabel}</TextLink>
            </>
          </Caption>}
        </>}
      </Main>}
      {!isComplementaryBetweenCell && showUrgencyLabel && <Adornment>
        <RoomsRemainingLabel roomCount={day.availableRooms} kind="plain" />
      </Adornment>}
      {!isComplementaryBetweenCell && hasPromo && !isBetweenDay && !isCheckIn && !isCheckOut && <Adornment>
        <SolidUsdCircleIcon size="S" colour="highlight-secondary" />
      </Adornment>}
    </CellWrapper>
  )
}

export default React.memo(OfferBundleAndSaveCalendarSidebarDayCell)
